import { useParams, Link } from 'react-router-dom'
import type { Application, ApplicationStatus, Currency, RejectReason, SalaryPeriod, SalaryRange, SubscriptionPlan, UserSettings } from '../types'
import { ApplicationDetailPage } from './ApplicationDetailPage'

type Props = {
  applications: Application[]
  onToggleStage: (appId: string, stageId: string) => void
  onUpdateStages: (appId: string, stages: Application['processStages']) => void
  onUpdateCompensation: (
    appId: string,
    payload: {
      salaryRange: SalaryRange
      targetAmount: number | null
      targetPeriod: SalaryPeriod
      targetCurrency: Currency
    }
  ) => Promise<void> | void
  onUpdateStatus: (appId: string, status: ApplicationStatus, offerReason?: string | null) => void
  onUpdateNote: (appId: string, note: string) => void
  onUpdateDetails: (appId: string, details: { contractType?: string | null; benefits?: string | null; techStack?: string[] }) => void
  onArchive: (appId: string) => void
  onUnarchive: (appId: string) => void
  onSubmitReject: (appId: string, reason: RejectReason) => void
  onToggleFavorite: (appId: string) => void
  settings: UserSettings
  plan: SubscriptionPlan
}

export const ApplicationDetailPageWrapper = ({
  applications,
  onToggleStage,
  onUpdateStages,
  onUpdateCompensation,
  onUpdateStatus,
  onUpdateNote,
  onUpdateDetails,
  onArchive,
  onUnarchive,
  onSubmitReject,
  onToggleFavorite,
  settings,
  plan,
}: Props) => {
  const { id } = useParams<{ id: string }>()

  const application = applications.find((app) => app.id === id)

  if (!application) {
    return (
      <div className="min-h-screen bg-app-surface flex items-center justify-center">
        <div className="card p-6 text-center">
          <h1 className="text-xl font-bold text-app-ink mb-2 font-display">Application not found</h1>
          <p className="text-app-muted mb-4">The application you're looking for doesn't exist.</p>
          <Link
            to="/"
            className="inline-block rounded-lg bg-app-primary px-4 py-2 text-sm font-semibold text-white hover:bg-app-primary-hover transition-colors"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  return (
    <ApplicationDetailPage
      application={application}
      onToggleStage={onToggleStage}
      onUpdateStages={onUpdateStages}
      onUpdateCompensation={onUpdateCompensation}
      onUpdateStatus={onUpdateStatus}
      onUpdateNote={onUpdateNote}
      onUpdateDetails={onUpdateDetails}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      onSubmitReject={onSubmitReject}
      onToggleFavorite={onToggleFavorite}
      settings={settings}
      plan={plan}
    />
  )
}
